import type { MouseEventHandler } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import { Button, CardBlock } from '@elfsight/widget-ui';
import { getInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useTranslation } from 'react-i18next';
import { useRect } from 'react-use-rect';
import type { Job } from '../../../../types';
import { usePopup } from '../../../popup/PopupProvider';

interface ButtonsProps {
  data: Partial<Job>;
  direction?: 'row' | 'column' | 'row-reverse';
  isShowApplyButton?: boolean;
  isShowLearnMoreButton?: boolean;
  isShowDescription?: boolean;
  isShowSkills?: boolean;
  fontSize?: number;
  fontWeight?: TextStyleValue['fontWeight'];
  color?: string;
  titleHeight?: number;
  isPopup?: boolean;
  isVertical?: boolean;
  cardWidth?: number;
}

export const Buttons = ({
  data,
  direction = 'row',
  isShowApplyButton = false,
  isShowLearnMoreButton = false,
  isShowDescription,
  isShowSkills,
  fontSize: derivedFontSize,
  fontWeight: derivedFontWeight,
  color,
  titleHeight,
  isPopup = false,
  isVertical = false,
  cardWidth
}: ButtonsProps) => {
  const { id, link, description, skills } = data;
  const {
    jobButtonsStyle,
    jobButtonRadius,
    applyButtonBackgroundColor,
    applyButtonTextColor,
    learnMoreButtonBackgroundColor,
    learnMoreButtonTextColor,
    popupEnable,
    editMode
  } = useSettings();

  const isShowDescriptionOrSkills =
    (description && isShowDescription) ||
    (Number(skills?.length) > 0 && isShowSkills);
  const fontSize = derivedFontSize ?? jobButtonsStyle.fontSize;
  const fontWeight = derivedFontWeight ?? jobButtonsStyle.fontWeight ?? 'bold';

  const { handleShowPopup } = usePopup();
  const onClick = handleShowPopup(id!);

  const needToChangeHeight =
    (!isShowApplyButton || !link || !isShowLearnMoreButton) &&
    isShowDescriptionOrSkills;

  const [width, setWidth] = useState(0);
  const [ref] = useRect(({ width }) => setWidth(width), {
    resize: true
  });

  const { t } = useTranslation();

  const applyClickHandler: MouseEventHandler = (e) => {
    e.stopPropagation();

    if (!link?.rawValue) {
      return;
    }

    const target = editMode || !link.target ? '_blank' : link.target;
    // Необходимо для открытия окна с email на iPhone
    const windowRef = window.open(link.rawValue, target);
    if (windowRef) windowRef.location = link.rawValue || '';
  };

  const learnMoreClickHandler: MouseEventHandler = (e) => {
    e.stopPropagation();
    onClick?.();
  };

  if (!isShowApplyButton && !isShowLearnMoreButton) return null;

  return (
    <ButtonsComponent
      ref={ref}
      _direction={width >= 300 ? direction : 'column'}
      _height={needToChangeHeight ? titleHeight : undefined}
      _isPopup={isPopup}
      _isVertical={isVertical}
      _cardWidth={cardWidth}
    >
      {isShowApplyButton && link && (
        <Button
          mode="contain"
          font={{
            ...jobButtonsStyle,
            fontSize,
            fontWeight
          }}
          color={color ?? applyButtonBackgroundColor}
          fontColor={color ? getInvertedColor(color) : applyButtonTextColor}
          borderRadius={jobButtonRadius}
          verticalPadding={fontSize * 0.5} // 0.5rem
          horizontalPadding={12}
          onClick={applyClickHandler}
          width="100%"
        >
          {t('Apply Now')}
        </Button>
      )}

      {isShowLearnMoreButton && popupEnable && (
        <Button
          mode="contain"
          font={{
            ...jobButtonsStyle,
            fontSize,
            fontWeight
          }}
          color={learnMoreButtonBackgroundColor}
          fontColor={learnMoreButtonTextColor}
          borderRadius={jobButtonRadius}
          verticalPadding={fontSize * 0.5} // 0.5rem
          horizontalPadding={12}
          onClick={learnMoreClickHandler}
          width="100%"
        >
          {t('Learn More')}
        </Button>
      )}
    </ButtonsComponent>
  );
};

export const ButtonsComponent = styled(CardBlock)<{
  _direction?: 'row' | 'column' | 'row-reverse';
  _isPopup?: boolean;
  _isVertical?: boolean;
  _height?: number;
  _cardWidth?: number;
}>`
  &&& {
    align-items: end;
    flex-direction: ${({ _direction }) => _direction ?? 'row'};

    ${({ _isPopup, _height, _isVertical }) =>
      !_isPopup &&
      !_isVertical &&
      css`
        padding: 20px 20px 20px 0;

        ${_height &&
        css`
          height: ${_height ? `${_height - 20}px` : 'auto'};
          flex: 0 0 ${_height ? `${_height - 20}px` : 'auto'};
          margin-top: 20px;
          margin-bottom: 20px;
          padding-bottom: 0;
          padding-top: 0;
          justify-content: center;
        `}
      `}

    box-sizing: border-box;
    align-self: flex-end;
    justify-content: ${({ _isVertical }) =>
      _isVertical ? 'end' : 'flex-start'};
    width: 100%;
  }

  button {
    max-width: ${({ _cardWidth, _isPopup }) =>
      !_isPopup && Number(_cardWidth) >= 560 ? '148px' : 'none'};
    min-width: 0;
  }

  button span {
    width: 100%;
    box-sizing: border-box;
  }

  button:first-child:not(:last-child) {
    ${({ _direction }) => {
      switch (_direction) {
        case 'row':
          return 'margin-right: 12px;';
        case 'row-reverse':
          return 'margin-left: 12px;';
        default:
          return 'margin-bottom: 8px';
      }
    }}
  }
`;
